import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { Web3Link } from '@web3/components';
import { formatNumber, web3Navigate, isNlSite } from '@web3/utils';
import { usePopoverState } from '../../../hooks';
import { useShowBalance } from '../../../store/dialogs';
import { useLocale } from '../../../store/i18nMessage';
import { useCurrentWallet } from '../../../store/userWallet';
import { scTrack } from '../../../utils/scTrack';
import { iconCaretDown } from '../../commonIcon';
import './style.scss';
import { Web3DepositModal } from '../../Web3DepositModal';
import { useState } from 'react';

export type AssetsDropProps = {};

export function AssetsDrop(_props: AssetsDropProps) {
  const { t, locale } = useLocale();
  const { isNone, totalBalance } = useCurrentWallet();
  const popoverState = usePopoverState(false, false);

  const { visible: showBalance, show: setShowBalance, hide: hideBalance } = useShowBalance();
  const [depositVisible, setDepositVisible] = useState(false);

  const handleToggleShowAssets = () => {
    showBalance ? hideBalance(false) : setShowBalance();
  };

  if (isNone) {
    return null;
  }

  const scProps = (o: string) => ({
    'data-spm-even': 'SpotWeb3NavigationBarClick',
    'data-spm-data': JSON.stringify({ assets_option: o }),
  });

  return (
    <>
      <Popover open={popoverState.visible}>
        <PopoverTrigger asChild>
          <Web3Link
            className="nav-link flex items-center !outline-none"
            href={`/${locale}/web3/balance`}
            {...scProps('Assets')}
            onMouseEnter={popoverState.show}
            onMouseLeave={popoverState.hide}
          >
            <span className="text-bds-static-white">{t('assets')}</span>
            <div className="size-3 text-bds-gray-t3 svg-full">{iconCaretDown}</div>
          </Web3Link>
        </PopoverTrigger>
        <PopoverContent asChild forceMount align="center" onOpenAutoFocus={(e) => e.preventDefault()}>
          {popoverState.domVisible && (
            <div
              className="web3-assets-popper ltr:mr-2 rtl:ml-2"
              style={{
                transition: 'opacity ease-in 100ms',
                opacity: popoverState.visible ? 1 : 0,
              }}
              onMouseEnter={popoverState.show}
              onMouseLeave={popoverState.hide}
              onTransitionEnd={popoverState.destroy}
            >
              <div className="assets-popper-top">
                <div
                  className="assets-overview cursor-pointer"
                  onClick={() => {
                    web3Navigate(`/${locale}/web3/balance`);
                    scTrack('SpotWeb3NavigationBarClick', {
                      assets_option: 'Assets',
                    });
                  }}
                >
                  <div className="flex items-center text-bds-gray-t2 gap-1">
                    <p className="text-sm">{t('wallet_assets')}</p>
                    <button
                      className=" bg-transparent ltr:ml-1 rtl:mr-1 size-3 text-bds-gray-t2 inline-flex svg-full p-0"
                      {...scProps('HideAmount')}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleShowAssets();
                      }}
                    >
                      {showBalance ? iconEyeOn : iconEyeOff}
                    </button>
                  </div>
                  <div className="flex items-baseline mt-2">
                    <p className="assets-amount">
                      {showBalance
                        ? totalBalance
                          ? formatNumber(totalBalance, {
                              decimal: 2,
                              noTrailZero: true,
                            })
                          : '--'
                        : '****'}
                    </p>
                    {showBalance && <p className="ltr:ml-1 rtl:mr-1 px-1 text-sm">USD</p>}
                  </div>
                </div>
                <p className="mt-2 text-bds-gray-t3 text-xs">*{t('assets_update_delay')}</p>
                <div className="mt-5 flex assets-shortcut">
                  {!isNlSite() ? (
                    <button
                      onClick={() => {
                        setDepositVisible(true);
                      }}
                      {...scProps('Deposit')}
                    >
                      <div className="svg-full w-4">{iconDownload}</div>
                      <p className="text-white text-xs">{t('deposit')}</p>
                    </button>
                  ) : null}

                  <Web3Link href={`/${locale}/web3/buy-crypto`} {...scProps('BuyCrypto')}>
                    <div className="svg-full w-4 text-transparent">{iconAssetsTool}</div>
                    <p className="text-white text-xs">{t('nav_crypto')}</p>
                  </Web3Link>
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
      <Web3DepositModal visible={depositVisible} onClose={() => setDepositVisible(false)} />
    </>
  );
}

const iconDownload = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.00001 9.33325C2.3682 9.33325 2.66668 9.63173 2.66668 9.99992V12.6666C2.66668 12.8434 2.73691 13.013 2.86194 13.138C2.98696 13.263 3.15653 13.3333 3.33334 13.3333H12.6667C12.8435 13.3333 13.0131 13.263 13.1381 13.138C13.2631 13.013 13.3333 12.8434 13.3333 12.6666V9.99992C13.3333 9.63173 13.6318 9.33325 14 9.33325C14.3682 9.33325 14.6667 9.63173 14.6667 9.99992V12.6666C14.6667 13.197 14.456 13.7057 14.0809 14.0808C13.7058 14.4559 13.1971 14.6666 12.6667 14.6666H3.33334C2.80291 14.6666 2.2942 14.4559 1.91913 14.0808C1.54406 13.7057 1.33334 13.197 1.33334 12.6666V9.99992C1.33334 9.63173 1.63182 9.33325 2.00001 9.33325Z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.19526 6.19526C4.45561 5.93491 4.87772 5.93491 5.13807 6.19526L8 9.05719L10.8619 6.19526C11.1223 5.93491 11.5444 5.93491 11.8047 6.19526C12.0651 6.45561 12.0651 6.87772 11.8047 7.13807L8.4714 10.4714C8.21106 10.7318 7.78894 10.7318 7.5286 10.4714L4.19526 7.13807C3.93491 6.87772 3.93491 6.45561 4.19526 6.19526Z"
      fill="#F7A600"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.00001 1.33325C8.3682 1.33325 8.66668 1.63173 8.66668 1.99992V9.99992C8.66668 10.3681 8.3682 10.6666 8.00001 10.6666C7.63182 10.6666 7.33334 10.3681 7.33334 9.99992V1.99992C7.33334 1.63173 7.63182 1.33325 8.00001 1.33325Z"
      fill="#F7A600"
    />
  </svg>
);
const iconAssetsTool = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1.33334" y="3.33325" width="13.3333" height="9.33333" rx="0.666667" stroke="white" strokeWidth="1.33333" strokeLinecap="round" />
    <line x1="4.00004" y1="5.99984" x2="6.00004" y2="5.99984" stroke="#F7A600" strokeWidth="1.33333" strokeLinecap="round" />
    <circle cx="9.33336" cy="9.33333" r="1.33333" fill="#F0960E" />
    <circle cx="11.3334" cy="9.33333" r="1.33333" fill="#F7A600" />
  </svg>
);

const iconEyeOn = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="icon_eye_on">
      <path
        id="Union"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.0001 19C6.9937 19 3.83195 14.6909 2.17028 12C3.83195 9.30913 6.9937 5 12.0001 5C17.0064 5 20.1682 9.30913 21.8299 12C20.1682 14.6909 17.0065 19 12.0001 19ZM12.0001 3C5.77923 3 2.09084 8.31968 0.460383 10.9625C0.0678924 11.5987 0.0678937 12.4013 0.460384 13.0375C2.09084 15.6803 5.77923 21 12.0001 21C18.2209 21 21.9093 15.6803 23.5398 13.0375C23.9323 12.4013 23.9323 11.5987 23.5398 10.9625C21.9093 8.31968 18.2209 3 12.0001 3ZM10.0001 12C10.0001 10.8954 10.8955 10 12.0001 10C13.1046 10 14.0001 10.8954 14.0001 12C14.0001 13.1046 13.1046 14 12.0001 14C10.8955 14 10.0001 13.1046 10.0001 12ZM12.0001 8C9.79094 8 8.00008 9.79086 8.00008 12C8.00008 14.2091 9.79094 16 12.0001 16C14.2092 16 16.0001 14.2091 16.0001 12C16.0001 9.79086 14.2092 8 12.0001 8Z"
        fill="#ADB1B8"
      />
    </g>
  </svg>
);
const iconEyeOff = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="icon_eye_off">
      <path
        id="Union"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.70711 2.29295C3.31658 1.90243 2.68342 1.90243 2.29289 2.29295C1.90237 2.68348 1.90237 3.31664 2.29289 3.70717L4.54471 5.95899C2.62202 7.62546 1.28072 9.63279 0.460383 10.9625C0.0678924 11.5987 0.0678937 12.4013 0.460384 13.0375C2.09084 15.6803 5.77923 21 12.0001 21C14.278 21 16.2164 20.2867 17.8385 19.2527L20.2929 21.7072C20.6834 22.0977 21.3166 22.0977 21.7071 21.7072C22.0976 21.3166 22.0976 20.6835 21.7071 20.293L19.4553 18.0411C21.3781 16.3746 22.7194 14.3672 23.5398 13.0375C23.9323 12.4013 23.9323 11.5987 23.5398 10.9625C21.9093 8.31968 18.2209 3 12.0001 3C9.72206 3 7.78364 3.71334 6.16151 4.74736L3.70711 2.29295ZM5.96311 7.37739C4.27224 8.80995 3.02351 10.6183 2.17028 12C3.83195 14.6909 6.9937 19 12.0001 19C13.6591 19 15.1155 18.5268 16.3819 17.7962L14.0318 15.4461C13.4363 15.7978 12.7412 16 12.0001 16C9.79094 16 8.00008 14.2092 8.00008 12C8.00008 11.2585 8.20238 10.5636 8.55401 9.96829L5.96311 7.37739ZM10.0677 11.4819C10.0235 11.6471 10.0001 11.8208 10.0001 12C10.0001 13.1046 10.8955 14 12.0001 14C12.1795 14 12.3531 13.9766 12.5182 13.9325L10.0677 11.4819ZM18.0369 16.6227L7.6181 6.20395C8.88455 5.47322 10.341 5 12.0001 5C17.0064 5 20.1682 9.30913 21.8299 12C20.9766 13.3817 19.7278 15.1901 18.0369 16.6227Z"
        fill="#ADB1B8"
      />
    </g>
  </svg>
);
